/**
 * Token de eliminación para los archivos del cajón de un documento.
 *
 * Pedido por las supervisoras (reunión 2026-09-18): las auxiliares suben una
 * contabilización, al mes se dan cuenta de que quedó mal, la borran y vuelven
 * a cargarla sin avisar. Cuando el cliente ya pagó contra la primera versión,
 * la supervisora se entera tarde y sin rastro de qué pasó.
 *
 * La solución acordada es reusar el token mensual que ya protege el borrado
 * de carpetas: lo tienen solo las supervisoras y se renueva el día 1 de cada
 * mes. Sin token no se borra contabilización, ni vinculado, ni XML.
 *
 * Este módulo NO valida el token: lo valida la callable
 * `validateDeletionToken` contra el token activo en Firestore, y de paso
 * registra el uso en `tokens/{id}/usages` (quién, cuándo, qué ítem, qué
 * cliente). Ese registro es justamente la trazabilidad que faltaba.
 *
 * ALCANCE, para que quede escrito: esto es una reja de interfaz. El borrado
 * de estos archivos corre en el navegador con los permisos del propio
 * usuario (solo el de CARPETAS corre server-side vía startFolderDeletion),
 * así que alguien con las herramientas de desarrollo abiertas puede saltarse
 * el modal. Frena el accidente y el "borro y recargo sin decir nada", que es
 * el problema real reportado; no frena a alguien decidido. Blindarlo de
 * verdad exige mover el borrado a una Cloud Function y ajustar las rules.
 *
 * El uso queda registrado al VALIDAR, no al borrar: si el token es válido y
 * el borrado falla después, queda un uso sin borrado. Es el mismo
 * comportamiento del flujo de carpetas (tree.js) y se mantiene igual a
 * propósito, para no tener dos semánticas distintas del mismo token.
 *
 * Módulo puro: sin React/Redux. El `customerId` lo calcula quien llama, con
 * el `extractCustomerId` de actions/database/trash.js, para no arrastrar las
 * acciones hasta aquí.
 */

/** Las tres cosas borrables del cajón. Viaja al backend como `itemType`. */
export const DELETION_ITEM_TYPES = {
  ACCOUNTING: 'contabilizacion',
  LINK: 'vinculado',
  XML: 'xml',
};

const VALID_ITEM_TYPES = new Set(Object.values(DELETION_ITEM_TYPES));

const GENERIC_ERROR = 'No se pudo validar el token. Revisa la conexión e intenta de nuevo.';

/**
 * Valida el token contra el backend antes de permitir un borrado.
 * Resuelve si el token es válido; lanza Error con mensaje en español si no.
 *
 * @param {object|function} firebase  objeto extendido (useFirebase) o factory (thunks)
 * @param {object}   params
 * @param {string}   params.token         lo que escribió el usuario
 * @param {string}   params.itemType      uno de DELETION_ITEM_TYPES
 * @param {string}  [params.itemName]     nombre del archivo, para el registro de uso
 * @param {string}  [params.customerId]   extractCustomerId(ref.path)
 * @param {string}  [params.customerName] razón social, para leer el registro sin cruzar ids
 */
export async function validateDeletionToken(firebase, {
  token,
  itemType,
  itemName,
  customerId,
  customerName,
} = {}) {
  if (!VALID_ITEM_TYPES.has(itemType)) {
    // Error de programación, no del usuario: que reviente en desarrollo.
    throw new Error(`itemType inválido: ${itemType}`);
  }

  const clean = String(token || '').trim().toUpperCase();
  if (!clean) {
    throw new Error('Ingresa el token de eliminación.');
  }

  // useFirebase() entrega el objeto extendido; en thunks llega el factory.
  const fb = typeof firebase === 'function' ? firebase() : firebase;

  try {
    await fb.functions().httpsCallable('validateDeletionToken')({
      token: clean,
      itemType,
      // null y no undefined: undefined rompe la serialización de la callable.
      itemName: itemName || null,
      customerId: customerId || null,
      customerName: customerName || null,
    });
  } catch (error) {
    // El backend ya responde en español ("Token inválido…", "No hay un token
    // activo…"); esos mensajes se muestran tal cual.
    throw new Error(error?.message || GENERIC_ERROR);
  }
}
